import { wordIndexAtTime } from '@src/services/reel/reelSectionWords';
import React, { useLayoutEffect, useRef } from 'react';
import styled from 'styled-components';

import type { ReelSectionWord } from '@src/services/reel/reelSectionWords';

interface ScrubTranscriptStripProps {
  /** The scrubbed Section's words, timed against the reel. */
  words: ReelSectionWord[];
  /** Reel time under the pointer — what is being said there. */
  time: number;
  testId?: string;
}

/**
 * How many words either side of the highlighted one are rendered.
 *
 * Enough to fill the strip and run off both edges under the fade; a Section can
 * hold hundreds of words, and all of them mounted would be a few hundred spans
 * repainting on every pointer move.
 */
const WINDOW_WORDS = 8;

/**
 * PLAT-11073 — the transcript under the scrub readout: the words around the
 * pointer, with the one being spoken there held in the middle and marked gold.
 *
 * The highlight does not travel — the line does. Whichever word the pointer
 * lands in is centred and the rest slide out from under it, so the creator
 * reads one fixed place instead of chasing a cursor along a line of text.
 *
 * Placement is imperative rather than state: the transform is a measurement of
 * the DOM this render just produced, and routing it through state would repaint
 * the row a second time for every word the scrub crosses. Nothing else about
 * the strip is written from here.
 */
export const ScrubTranscriptStrip: React.FC<ScrubTranscriptStripProps> = ({
  words,
  time,
  testId,
}) => {
  const runRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLSpanElement>(null);
  const isPlacedRef = useRef(false);

  const activeIndex = wordIndexAtTime(words, time);
  const from = Math.max(0, activeIndex - WINDOW_WORDS);
  const to = Math.min(words.length, activeIndex + WINDOW_WORDS + 1);

  // No dependency list: every render has a new word under the pointer to centre,
  // and the measurement is two layout reads.
  useLayoutEffect(() => {
    const run = runRef.current;
    const active = activeRef.current;
    if (!run || !active) return;

    const centre = active.offsetLeft + active.offsetWidth / 2;

    // The first placement is not a movement. Left to the transition, the strip
    // would slide in from wherever the untranslated run happened to sit the
    // moment the readout appeared.
    if (!isPlacedRef.current) {
      run.style.transition = 'none';
      run.style.transform = `translateX(${-centre}px)`;
      // Forces the style through before the transition is handed back, so the
      // two writes are not coalesced into one animated change.
      void run.offsetWidth;
      // Back to the stylesheet's rule — which is also what keeps the
      // reduced-motion override in effect.
      run.style.transition = '';
      isPlacedRef.current = true;
      return;
    }

    run.style.transform = `translateX(${-centre}px)`;
  });

  return (
    <Viewport data-testid={testId}>
      <Run ref={runRef}>
        {words.slice(from, to).map((word, offset) => {
          const index = from + offset;
          const isActive = index === activeIndex;
          return (
            <Word
              key={index}
              ref={isActive ? activeRef : undefined}
              $isActive={isActive}
              data-active={isActive || undefined}
            >
              {word.value}
            </Word>
          );
        })}
      </Run>
    </Viewport>
  );
};

/**
 * The window the words rotate through. Its fade is what says the line runs on
 * past both edges rather than starting and stopping where the readout does.
 */
const Viewport = styled.div`
  position: relative;
  /* The readout hugs the times line above, so filling it is what keeps the two
     rows the same width — and a percentage contributes nothing to that
     shrink-to-fit, so the words never widen the box. */
  width: 100%;
  height: 15px;
  margin-top: 3px;
  overflow: hidden;
  mask-image: linear-gradient(
    to right,
    transparent,
    black 20%,
    black 80%,
    transparent
  );
  -webkit-mask-image: linear-gradient(
    to right,
    transparent,
    black 20%,
    black 80%,
    transparent
  );
`;

/**
 * The line of words, hung from the middle of the viewport and pulled sideways
 * by however far into it the highlighted word sits — which is what keeps that
 * word centred whichever one it is.
 */
const Run = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  display: flex;
  gap: 4px;
  white-space: nowrap;
  line-height: 15px;
  will-change: transform;
  transition: transform 0.12s ease-out;

  /* The words still rotate; they just arrive rather than travel. */
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/**
 * Gold on the word being spoken — the tracker's own colour, and the design
 * system's for visual indication, so nothing here reads as something to click.
 */
const Word = styled.span<{ $isActive: boolean }>`
  color: ${props => (props.$isActive ? 'var(--accent)' : 'var(--text-muted)')};
  font-weight: ${props => (props.$isActive ? 600 : 400)};
`;
